import React, { useCallback } from "react"
import {
  faPlus,
  faTrash,
  faCopy,
  faExclamationTriangle,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { Slide } from "types/ReportingDashboard/reporting.types"

/**
 * Props for SlideThumb component
 */
interface SlideThumbProps {
  /** The 0-based index of the slide */
  index: number
  /** The slide data object */
  slide: Slide
  /** Whether this slide is currently active */
  isActive: boolean
  /** Callback when the thumbnail is clicked */
  onClick: (slide: Slide) => void
  /** Callback when the delete button is clicked */
  onDelete: (slideId: string) => void
  /** Callback when the duplicate button is clicked */
  onDuplicate: (slide: Slide) => void
  /** Whether the parent is in template editing mode */
  isTemplateMode: boolean
  /** The name of the template applied to this slide */
  templateName: string | null
}

/**
 * Component representing a single slide thumbnail in the SlideManager.
 * Preserves the original layout and styling.
 */
const SlideThumb: React.FC<SlideThumbProps> = ({
  index,
  slide,
  isActive,
  onClick,
  onDelete,
  onDuplicate,
  isTemplateMode,
  templateName,
}) => {
  // Memoize handlers to prevent unnecessary re-renders if passed down
  const handleClick = useCallback((): void => {
    // Prevent changing slides via thumbnail click when in template edit mode
    if (!isTemplateMode) {
      onClick(slide)
    }
  }, [onClick, slide, isTemplateMode])

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation() // Prevent triggering onClick
      onDelete(slide.id)
    },
    [onDelete, slide.id]
  )

  const handleDuplicate = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation() // Prevent triggering onClick
      onDuplicate(slide)
    },
    [onDuplicate, slide]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        handleClick()
      }
    },
    [handleClick]
  )

  return (
    // Main container div with dynamic classes for styling based on state
    <div
      className={`group relative p-2 mb-2 cursor-pointer rounded ${
        isActive
          ? isTemplateMode // TODO Nested ternary - can improve readability with a function
            ? "bg-blue-50 border border-[#36C3ED]" // Active in template mode (likely disabled look)
            : "bg-blue-50 border border-[#36C3ED]" // Active in normal mode
          : "hover:bg-gray-50 border border-gray-200" // Inactive state
      }`}
      onClick={handleClick} // Use the memoized handler
      onKeyDown={handleKeyDown}
      role="button"
      aria-current={isActive ? "page" : undefined}
      aria-label={`Slide ${index + 1}${
        templateName ? ` with template ${templateName}` : ""
      }${isActive ? " (Current Slide)" : ""}`}
      tabIndex={isTemplateMode ? -1 : 0} // Remove from tab order in template mode
    >
      {/* Slide Preview Area */}
      <div className="w-full bg-white aspect-video rounded border border-gray-200 mb-1">
        <div className="p-1 text-xs text-gray-500 flex justify-between items-center">
          {/* Page Number */}
          <span>Page {index + 1}</span>
          {/* Template Name (if applicable) */}
          {templateName && (
            <span className="text-xs text-blue-500 flex items-center">
              <FontAwesomeIcon icon={faLayerGroup} className="mr-1" size="xs" />
              {templateName}
            </span>
          )}
        </div>
      </div>

      {/* Action Buttons (Duplicate, Delete) */}
      {/* Conditionally rendered based on template mode */}
      {!isTemplateMode && (
        // Container for buttons, shown on hover
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {/* Duplicate Button */}
          <button
            onClick={handleDuplicate} // Use memoized handler
            className="p-1 text-gray-500 hover:text-[#36C3ED]"
            title="Duplicate slide"
            aria-label={`Duplicate slide ${index + 1}`}
          >
            <FontAwesomeIcon icon={faCopy} className="text-xs" />
          </button>
          {/* Delete Button */}
          <button
            onClick={handleDelete} // Use memoized handler
            className="p-1 text-gray-500 hover:text-red-500"
            title="Delete slide"
            aria-label={`Delete slide ${index + 1}`}
          >
            <FontAwesomeIcon icon={faTrash} className="text-xs" />
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Props for SlideManager component
 */
export interface SlideManagerProps {
  /** Array of slide objects */
  slides?: Slide[]
  /** ID of the currently active slide */
  activeSlideId: string | null
  /** Callback when a slide thumbnail is clicked */
  onSlideChange: (slide: Slide) => void
  /** Callback when the add slide button is clicked */
  onSlideAdd: () => void
  /** Callback when a slide's delete button is clicked */
  onSlideDelete: (slideId: string) => void
  /** Callback when a slide's duplicate button is clicked */
  onSlideDuplicate: (slide: Slide) => void
  /** Whether a template is currently active (influences add button title) */
  hasTemplate: boolean
  /** Whether the parent is in template editing mode */
  isTemplateMode?: boolean
  /** Function to get the template name for a given slide */
  getTemplateNameForSlide: (slide: Slide) => string | null
}

/**
 * Slide Manager Component
 *
 * Manages the display and interaction of slide thumbnails in the right sidebar.
 * Provides slide management functionality including add, delete, duplicate operations.
 * Supports template mode with appropriate UI feedback and restrictions.
 *
 * @param props - Component props
 * @returns JSX element for slide management interface
 */
const SlideManager: React.FC<SlideManagerProps> = ({
  slides = [], // Default to empty array
  activeSlideId,
  onSlideChange,
  onSlideAdd,
  onSlideDelete,
  onSlideDuplicate,
  hasTemplate,
  isTemplateMode = false,
  getTemplateNameForSlide,
}) => {
  // Memoize the callback for saving template changes to avoid redefining on every render
  const triggerSaveTemplateChanges = useCallback((): void => {
    window.dispatchEvent(new CustomEvent("saveTemplateChanges"))
  }, [])

  return (
    // Main container div - Make it a vertical flex container
    // Add flex flex-col to allow the scrollable area to grow
    <div className="flex flex-col flex-shrink-0 p-4 h-full">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-md font-semibold m-0">Pages</h3>

        {/* Conditional rendering for Template Mode indicator or Add button */}
        {isTemplateMode ? (
          // Template Mode Indicator
          <div className="py-1 px-2 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-600 flex items-center">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="mr-1"
              size="xs"
            />
            Template Mode
          </div>
        ) : (
          // Add Slide Button
          <div className="flex">
            <button
              onClick={onSlideAdd}
              title={
                hasTemplate ? "Add new slide with template" : "Add new slide"
              }
              className={`p-2 text-gray-500 hover:text-[#36C3ED] rounded ${
                hasTemplate ? "bg-blue-50" : "" // Apply background if template active
              }`}
              aria-label="Add new slide"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        )}
      </div>

      {/* Scrollable list container for slide thumbnails */}
      <div className="flex-grow space-y-2 overflow-y-auto pb-32 pr-1">
        {/* Map over slides array to render thumbnails */}
        {slides.map((slide: Slide, index: number) => (
          <SlideThumb
            key={slide.id || index} // Use slide ID as key, fallback to index
            index={index}
            slide={slide}
            isActive={slide.id === activeSlideId}
            onClick={onSlideChange}
            onDelete={onSlideDelete}
            onDuplicate={onSlideDuplicate}
            isTemplateMode={isTemplateMode}
            // Get template name using the provided function
            templateName={getTemplateNameForSlide(slide)}
          />
        ))}
      </div>

      {/* Message shown when there are no slides */}
      {slides.length === 0 && (
        <div className="p-4 text-gray-400 text-center">
          No Pages. Click + to add a page.
        </div>
      )}

      {/* Information panel shown only in template editing mode */}
      {isTemplateMode && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700">
          <p className="font-medium mb-1">Template Editing Mode</p>
          <p className="mb-0">
            Changes you make will only affect the template layer, not
            page-specific content.
          </p>
          {/* Button to trigger saving template changes */}
          <button
            onClick={triggerSaveTemplateChanges} // Use the memoized handler
            className="mt-2 w-full bg-blue-600 text-white hover:bg-blue-700 py-1 px-2 rounded text-xs font-medium transition-colors"
          >
            Save Template Changes
          </button>
        </div>
      )}
    </div>
  )
}

export default SlideManager
